import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Heading, Icon, Paragraph, Upsell } from '@zillow/constellation-10';
import { IconCalendarClockFilled } from '@zillow/constellation-icons';

import { Lease } from '../../models';
import { isAfter, startOfToday } from 'date-fns';
import { differenceInDaysBetweenDateAndToday } from '../../constants/functionConstants';

const MANAGE_LEASE_UPSELL_DISMISSED_KEY = 'manageLeaseUpsellDismissed_';

type Props = {
  lease: Lease;
};

export function shouldShowManageLeaseUpsell(lease: Lease): boolean {
  const daysUntilExpiration = lease.endDate ? differenceInDaysBetweenDateAndToday(lease.endDate) : null;
  const isWithin60Days = daysUntilExpiration !== null && daysUntilExpiration <= 60 && daysUntilExpiration >= 0;

  if (!isWithin60Days) {
    return false;
  }

  const dismissedValue = localStorage.getItem(`${MANAGE_LEASE_UPSELL_DISMISSED_KEY}${lease.id}`);
  return dismissedValue !== 'true';
}

export default function ManageYourLeaseUpsell({ lease }: Props): JSX.Element | null {
  const history = useHistory();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const dismissedValue = localStorage.getItem(`${MANAGE_LEASE_UPSELL_DISMISSED_KEY}${lease.id}`);
    if (dismissedValue === 'true') {
      setIsDismissed(true);
    }
  }, [lease.id]);

  function onManageYourLeaseClick() {
    history.push(Lease.getManageYourLeasePath(lease.id));
  }

  function onClose() {
    localStorage.setItem(`${MANAGE_LEASE_UPSELL_DISMISSED_KEY}${lease.id}`, 'true');
    setIsDismissed(true);
  }

  if (isDismissed || !shouldShowManageLeaseUpsell(lease)) {
    return null;
  }

  return (
    <Upsell
      header={
        <Heading level={2}>
          {isAfter(lease?.endDate, startOfToday())
            ? `What happens when your lease for ${lease.getLabel()} expires?`
            : `Your lease for ${lease.getLabel()} is expired`}
        </Heading>
      }
      body={<Paragraph>Review your options for managing your lease</Paragraph>}
      action={
        <Button tone="brand" emphasis="filled" onClick={onManageYourLeaseClick}>
          Manage your lease
        </Button>
      }
      closeable
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      tone="soft"
      media={
        <Icon size="lg">
          <IconCalendarClockFilled />
        </Icon>
      }
      elevated={false}
    />
  );
}
